import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { EMPTY, catchError, filter, finalize, forkJoin, map, of, switchMap, take, tap, timer } from 'rxjs';
import { StreamingEcsService, VbLxpAdminSettingsService } from './vb-lxp-admin-settings.service';

@Component({
	selector: 'vb-lxp-streaming-ecs-table',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './vb-lxp-streaming-ecs-table.component.html',
	styleUrls: ['./vb-lxp-streaming-ecs-table.component.css'],
})
export class VbLxpStreamingEcsTableComponent {
	private readonly adminSettingsService = inject(VbLxpAdminSettingsService);
	private readonly pollIntervalMs = 10000;

	@Input({ required: true }) services = signal<StreamingEcsService[]>([]);
	@Output() refreshServices = new EventEmitter<void>();
	
	private readonly actionsInProgress = signal<Set<string>>(new Set());
	private readonly bulkActionInProgress = signal(false);

	protected isServiceRunning(service: StreamingEcsService): boolean {
		return (service.runningCount ?? 0) > 0;
	}

	protected isActionInProgress(serviceId: string): boolean {
		return this.actionsInProgress().has(serviceId);
	}

	protected isBulkActionInProgress(): boolean {
		return this.bulkActionInProgress();
	}

	protected startService(service: StreamingEcsService): void {
		this.performServiceAction(service, 'start');
	}

	protected stopService(service: StreamingEcsService): void {
		this.performServiceAction(service, 'stop');
	}

	protected startAllServices(): void {
		this.performBulkAction('start');
	}

	protected stopAllServices(): void {
		this.performBulkAction('stop');
	}

	private performServiceAction(service: StreamingEcsService, action: 'start' | 'stop'): void {
		this.actionsInProgress.update(set => {
			const newSet = new Set(set);
			newSet.add(service.id);
			return newSet;
		});

		const request$ = action === 'start'
			? this.adminSettingsService.startStreamingEcsService(service.id)
			: this.adminSettingsService.stopStreamingEcsService(service.id);

		request$
			.pipe(
				tap((response) => {
					console.log(`[StreamingECS] Service ${action} initiated:`, response);
					
					// Optimistically update the service in the list
					this.services.update(services => {
						return services.map(s => {
							if (s.id === service.id) {
								return {
									...s,
									desiredCount: action === 'start' ? 1 : 0,
								};
							}
							return s;
						});
					});
				}),
				switchMap(() => this.waitForServicesToReachStatus([service.id], action)),
				catchError((error) => {
					console.error(`[StreamingECS] Error ${action}ing service:`, error);
					alert(`Failed to ${action} service: ${error.error?.error || error.message}`);
					return EMPTY;
				}),
				finalize(() => {
					this.actionsInProgress.update(set => {
						const newSet = new Set(set);
						newSet.delete(service.id);
						return newSet;
					});
				}),
			)
			.subscribe();
	}

	private performBulkAction(action: 'start' | 'stop'): void {
		this.bulkActionInProgress.set(true);

		const allServices = this.services();
		
		// Filter services based on action:
		// - For 'start': only include stopped services (runningCount === 0)
		// - For 'stop': only include running services (runningCount > 0)
		const servicesToProcess = allServices.filter(service => {
			if (action === 'start') {
				return (service.runningCount ?? 0) === 0;
			} else {
				return (service.runningCount ?? 0) > 0;
			}
		});

		if (servicesToProcess.length === 0) {
			console.log(`[StreamingECS] No services to ${action}`);
			this.bulkActionInProgress.set(false);
			return;
		}

		console.log(`[StreamingECS] Bulk ${action} - Processing ${servicesToProcess.length} services`);

		const requests = servicesToProcess.map(service => {
			const request$ = action === 'start'
				? this.adminSettingsService.startStreamingEcsService(service.id)
				: this.adminSettingsService.stopStreamingEcsService(service.id);

			// Catch individual errors so forkJoin doesn't fail completely
			return request$.pipe(
				tap((response) => {
					console.log(`[StreamingECS] Bulk ${action} - Service ${service.id}:`, response);
				}),
				catchError((error) => {
					console.error(`[StreamingECS] Bulk ${action} failed for service ${service.id}:`, error);
					// Return a successful observable to continue with other requests
					return of({ error: true, serviceId: service.id, message: error.message });
				}),
			);
		});

		forkJoin(requests)
			.pipe(
				tap((responses) => {
					console.log(`[StreamingECS] Bulk ${action} completed:`, responses);

					// Optimistically update only the processed services
					this.services.update(services => {
						return services.map(s => {
							const wasProcessed = servicesToProcess.some(ps => ps.id === s.id);
							if (wasProcessed) {
								return {
									...s,
									desiredCount: action === 'start' ? 1 : 0,
								};
							}
							return s;
						});
					});
				}),
				switchMap((responses) => {
					const failedIds = responses
						.filter((r: any) => r.error)
						.map((r: any) => r.serviceId);
					const successfulIds = servicesToProcess
						.map(service => service.id)
						.filter(id => !failedIds.includes(id));

					if (failedIds.length > 0) {
						console.warn(`[StreamingECS] ${failedIds.length} services failed to ${action}:`, failedIds);
					}

					if (successfulIds.length === 0) {
						console.warn(`[StreamingECS] No services successfully accepted bulk ${action} request`);
						return of(null);
					}

					return this.waitForServicesToReachStatus(successfulIds, action);
				}),
				finalize(() => {
					this.bulkActionInProgress.set(false);
				}),
			)
			.subscribe();
	}

	private waitForServicesToReachStatus(serviceIds: string[], action: 'start' | 'stop') {
		console.log(`[StreamingECS] Polling every ${this.pollIntervalMs / 1000}s for ${action} status`, serviceIds);

		return timer(this.pollIntervalMs, this.pollIntervalMs).pipe(
			switchMap(() =>
				this.adminSettingsService.getStreamingEcsServices().pipe(
					tap((response) => {
						this.services.set(response.services);
					}),
					map(response => response.services),
					catchError((error) => {
						console.error('[StreamingECS] Error polling service statuses:', error);
						return of(null);
					}),
				),
			),
			filter((services): services is StreamingEcsService[] => services !== null),
			filter((services) => this.haveAllServicesReachedDesiredStatus(services, serviceIds, action)),
			take(1),
			tap(() => {
				console.log(`[StreamingECS] Desired status reached for ${action}:`, serviceIds);
				this.refreshServices.emit();
			}),
		);
	}

	private haveAllServicesReachedDesiredStatus(
		services: StreamingEcsService[],
		serviceIds: string[],
		action: 'start' | 'stop',
	): boolean {
		return serviceIds.every((serviceId) => {
			const latestService = services.find(service => service.id === serviceId);
			if (!latestService) {
				return false;
			}

			if (action === 'start') {
				return (latestService.runningCount ?? 0) > 0;
			}

			return (latestService.runningCount ?? 0) === 0;
		});
	}
}
